import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

interface Node {
  id: string;
  label?: string;
  type?: string;
  biotype?: string;
  is_lncrna?: boolean;
  is_fda_target?: boolean;
  is_drug_target?: boolean;
  log_fc?: number;
  degree?: number;
  betweenness?: number;
}

interface Edge {
  source: string;
  target: string;
  interaction_type?: string;
  weight?: number;
}

interface GRNData {
  nodes: Node[];
  edges: Edge[];
  n_nodes?: number;
  n_edges?: number;
  condition?: string;
}

interface NetworkGraphProps {
  data: {
    nodes: Node[];
    edges: Edge[];
    has_normal_grn?: boolean;
    disease_grn?: GRNData;
    normal_grn?: GRNData;
    comparison?: any;
  };
}

const NetworkGraph: React.FC<NetworkGraphProps> = ({ data }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [viewMode, setViewMode] = useState<'disease' | 'normal'>('disease');

  // Download network as PNG
  const downloadPNG = () => {
    if (!svgRef.current) return;

    const svg = svgRef.current;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    canvas.width = 800;
    canvas.height = 600;

    img.onload = () => {
      ctx?.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `grn_${viewMode}_${Date.now()}.png`;
          link.click();
          URL.revokeObjectURL(url);
        }
      });
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Download network data as CSV
  const downloadCSV = () => {
    const currentData = viewMode === 'normal' && data.has_normal_grn && data.normal_grn
      ? data.normal_grn
      : { nodes: data.nodes, edges: data.edges };

    // Create CSV content
    const nodesCSV = [
      ['Gene', 'Type', 'FDA Target', 'Degree', 'Betweenness'].join(','),
      ...currentData.nodes.map((n: Node) => [
        n.id,
        n.is_lncrna ? 'lncRNA' : 'Protein-coding',
        n.is_fda_target ? 'Yes' : 'No',
        n.degree || 0,
        n.betweenness?.toFixed(3) || 0
      ].join(','))
    ].join('\n');

    const edgesCSV = [
      ['Source', 'Target', 'Type', 'Weight'].join(','),
      ...currentData.edges.map((e: Edge) => [
        e.source,
        e.target,
        e.interaction_type || 'scgpt',
        e.weight?.toFixed(3) || 0
      ].join(','))
    ].join('\n');

    const csvContent = `# Nodes\n${nodesCSV}\n\n# Edges\n${edgesCSV}`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `grn_${viewMode}_${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Helper to check if node is non-coding RNA
  const isNonCodingRNA = (node: Node): boolean => {
    return node.is_lncrna ||
           node.type === 'lncrna' ||
           node.biotype?.toLowerCase().includes('lncrna') ||
           node.biotype?.toLowerCase().includes('ncrna');
  };

  // Helper to get node color
  const getNodeColor = (node: Node): string => {
    if (node.is_lncrna) return '#3B82F6'; // Blue for lncRNA
    if (node.is_fda_target || node.is_drug_target) return '#F97316'; // Orange for FDA targets only
    return '#6B7280'; // Gray for all other genes
  };

  // Helper to get node stroke — colored border for DE genes (|log_fc| > 1)
  const getNodeStroke = (node: Node): { color: string; width: number } => {
    if (node.log_fc !== undefined && node.log_fc !== null) {
      if (node.log_fc > 1) return { color: '#EF4444', width: 3 };  // Red border for upregulated
      if (node.log_fc < -1) return { color: '#10B981', width: 3 }; // Green border for downregulated
    }
    return { color: '#fff', width: 2 };
  };

  // Helper to get node radius
  const getNodeRadius = (node: Node): number => {
    return (node.is_lncrna || node.is_fda_target) ? 12 : 8;
  };

  useEffect(() => {
    if (!svgRef.current || !data?.nodes || !data?.edges) return;

    // Clear existing content
    d3.select(svgRef.current).selectAll('*').remove();

    // Responsive dimensions based on container size
    const containerWidth = svgRef.current?.parentElement?.clientWidth || window.innerWidth * 0.9;
    const containerHeight = svgRef.current?.parentElement?.clientHeight || window.innerHeight * 0.7;
    const width = Math.max(containerWidth, 800);  // Minimum 800px
    const height = Math.max(containerHeight, 600); // Minimum 600px

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .style('background-color', '#f8fafc');

    // Add arrow markers for directionality
    svg.append('defs')
      .selectAll('marker')
      .data(['positive', 'negative', 'ppi', 'upregulated', 'downregulated'])
      .enter().append('marker')
      .attr('id', d => `arrow-${d}`)
      .attr('markerWidth', 10)
      .attr('markerHeight', 10)
      .attr('refX', 20)
      .attr('refY', 3)
      .attr('orient', 'auto')
      .attr('markerUnits', 'strokeWidth')
      .append('path')
      .attr('d', 'M0,0 L0,6 L9,3 z')
      .attr('fill', d => {
        if (d === 'positive' || d === 'upregulated') return '#10B981';
        if (d === 'negative' || d === 'downregulated') return '#EF4444';
        return '#3B82F6';
      });

    if (viewMode === 'normal' && data.has_normal_grn && data.disease_grn && data.normal_grn) {
      // Side-by-side comparison mode with actual normal GRN data
      renderSideBySideNetworks(svg, data.disease_grn, data.normal_grn, width, height);
    } else {
      // Single disease network view
      renderSingleNetwork(svg, data, width, height);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, viewMode]);

  const renderSingleNetwork = (svg: any, data: any, width: number, height: number) => {
    // Create container for zooming
    const g = svg.append('g');

    // Add zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 3])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom as any);

    // Add zoom control buttons
    const controlsDiv = document.createElement('div');
    controlsDiv.style.cssText = 'position: absolute; top: 10px; right: 10px; display: flex; gap: 5px; z-index: 10;';

    const zoomControls = [
      { label: '+', tooltip: 'Zoom In', scale: 1.3 },
      { label: '−', tooltip: 'Zoom Out', scale: 0.7 },
      { label: 'Reset', tooltip: 'Reset View', scale: null }
    ];

    zoomControls.forEach(({ label, tooltip, scale }) => {
      const btn = document.createElement('button');
      btn.textContent = label;
      btn.title = tooltip;
      btn.style.cssText = 'padding: 8px 12px; background: #374151; color: #f3f4f6; border: 1px solid #4b5563; border-radius: 4px; cursor: pointer; font-weight: 600; transition: all 0.2s;';
      btn.onmouseenter = () => btn.style.background = '#4b5563';
      btn.onmouseleave = () => btn.style.background = '#374151';
      btn.onclick = () => {
        if (scale === null) {
          svg.transition().duration(500).call(zoom.transform as any, d3.zoomIdentity);
        } else {
          svg.transition().duration(300).call(zoom.scaleBy as any, scale);
        }
      };
      controlsDiv.appendChild(btn);
    });

    svgRef.current?.parentElement?.appendChild(controlsDiv);

    // Filter edges to only include those with valid node references
    const nodeIds = new Set(data.nodes.map((n: Node) => n.id));
    const validEdges = data.edges.filter((e: Edge) => {
      const sourceId = typeof e.source === 'string' ? e.source : (e.source as any).id;
      const targetId = typeof e.target === 'string' ? e.target : (e.target as any).id;
      const isValid = nodeIds.has(sourceId) && nodeIds.has(targetId);
      if (!isValid) {
        console.warn(`Filtering out edge with missing node: ${sourceId} -> ${targetId}`);
      }
      return isValid;
    });

    // Only render nodes that participate in at least one edge (drop isolated nodes)
    const connectedNodeIds = new Set<string>();
    validEdges.forEach((e: Edge) => {
      const sourceId = typeof e.source === 'string' ? e.source : (e.source as any).id;
      const targetId = typeof e.target === 'string' ? e.target : (e.target as any).id;
      connectedNodeIds.add(sourceId);
      connectedNodeIds.add(targetId);
    });
    const connectedNodes = data.nodes.filter((n: Node) => connectedNodeIds.has(n.id));

    // Create simulation with boundary forces to keep nodes within viewBox
    const simulation = d3.forceSimulation(connectedNodes as any)
      .force('link', d3.forceLink(validEdges)
        .id((d: any) => d.id)
        .distance(100))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(30))
      .force('x', d3.forceX(width / 2).strength(0.1))  // Boundary force X
      .force('y', d3.forceY(height / 2).strength(0.1)); // Boundary force Y

    // Create edges
    const link = g.append('g')
      .selectAll('line')
      .data(validEdges)
      .enter()
      .append('line')
      .attr('stroke', (d: any) => {
        if (d.interaction_type === 'ppi') return '#3B82F6';
        if (d.interaction_type === 'positive') return '#10B981';
        if (d.interaction_type === 'negative') return '#EF4444';
        return '#94a3b8';
      })
      .attr('stroke-width', (d: any) => {
        const baseWidth = 1;
        const weight = d.weight || 0.5;
        return baseWidth + (weight * 3);
      })
      .attr('stroke-opacity', 0.6)
      .attr('stroke-dasharray', (d: any) =>
        d.interaction_type === 'ppi' ? '0' : '5,5'
      )
      .attr('marker-end', (d: any) => {
        // Add directional arrows based on fold change
        const targetNode = data.nodes.find((n: Node) => n.id === d.target);
        if (targetNode?.log_fc !== undefined && targetNode?.log_fc !== null && Math.abs(targetNode.log_fc) > 0.5) {
          return targetNode.log_fc > 0 ? 'url(#arrow-upregulated)' : 'url(#arrow-downregulated)';
        }
        const markerType = d.interaction_type || 'ppi';
        return `url(#arrow-${markerType})`;
      });

    // Create nodes (connected only)
    const node = g.append('g')
      .selectAll('g')
      .data(connectedNodes)
      .enter()
      .append('g')
      .call(d3.drag<any, any>()
        .on('start', (event, d: any) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d: any) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d: any) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }));

    // Render node shapes
    node.each(function(d: any) {
      const nodeGroup = d3.select(this);
      const isNcRNA = isNonCodingRNA(d);
      const radius = getNodeRadius(d);
      const fillColor = getNodeColor(d);
      const stroke = getNodeStroke(d);

      console.log(`🔍 NetworkGraph ${d.id}: is_lncrna=${d.is_lncrna}, biotype=${d.biotype}, isNcRNA=${isNcRNA}`);

      if (isNcRNA) {
        // Triangle for lncRNA
        const size = radius * 1.3;
        const height = size * 1.5;
        nodeGroup.append('path')
          .attr('d', `M 0,${-height} L ${size},${height/2} L ${-size},${height/2} Z`)
          .attr('fill', fillColor)
          .attr('stroke', stroke.color)
          .attr('stroke-width', stroke.width);
      } else {
        // Circle for protein-coding
        nodeGroup.append('circle')
          .attr('r', radius)
          .attr('fill', fillColor)
          .attr('stroke', stroke.color)
          .attr('stroke-width', stroke.width);
      }

      // FDA target highlighting
      if (d.is_fda_target) {
        if (isNcRNA) {
          const size = (radius + 5) * 1.3;
          const height = size * 1.5;
          nodeGroup.append('path')
            .attr('d', `M 0,${-height} L ${size},${height/2} L ${-size},${height/2} Z`)
            .attr('fill', 'none')
            .attr('stroke', '#F59E0B')
            .attr('stroke-width', 4)
            .attr('opacity', 0.8);
        } else {
          nodeGroup.append('circle')
            .attr('r', radius + 5)
            .attr('fill', 'none')
            .attr('stroke', '#F59E0B')
            .attr('stroke-width', 4)
            .attr('opacity', 0.8);
        }
      }
    });

    // Add node labels
    node.append('text')
      .text((d: Node) => d.label || d.id)
      .attr('x', 0)
      .attr('y', -18)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#334155');

    // Add title for tooltips
    node.append('title')
      .text((d: Node) => {
        const parts = [`${d.label || d.id}`];
        if (d.biotype) parts.push(`Biotype: ${d.biotype}`);
        if (d.is_fda_target) parts.push('FDA Drug Target');
        if (d.log_fc !== undefined && d.log_fc !== null) parts.push(`Log FC: ${d.log_fc.toFixed(2)}`);
        if (d.degree) parts.push(`Degree: ${d.degree}`);
        if (d.betweenness !== undefined && d.betweenness !== null) parts.push(`Betweenness: ${d.betweenness.toFixed(3)}`);
        return parts.join('\n');
      });

    // Update positions on simulation tick
    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      node
        .attr('transform', (d: any) => `translate(${d.x},${d.y})`);
    });

    // Auto-fit network to view on initial load
    simulation.on('end', () => {
      const xs = data.nodes.map((n: any) => n.x).filter((x: number) => !isNaN(x));
      const ys = data.nodes.map((n: any) => n.y).filter((y: number) => !isNaN(y));

      if (xs.length === 0 || ys.length === 0) return;

      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      const graphWidth = maxX - minX;
      const graphHeight = maxY - minY;

      // Calculate scale to fit with padding
      const padding = 50;
      const scaleX = (width - 2 * padding) / graphWidth;
      const scaleY = (height - 2 * padding) / graphHeight;
      const scale = Math.min(scaleX, scaleY, 1.0); // Don't zoom in beyond 100%

      // Calculate translation to center
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      const translateX = width / 2 - centerX * scale;
      const translateY = height / 2 - centerY * scale;

      // Apply transform smoothly
      svg.transition()
        .duration(750)
        .call(
          zoom.transform as any,
          d3.zoomIdentity.translate(translateX, translateY).scale(scale)
        );
    });

    // Cleanup
    return () => {
      simulation.stop();
    };
  };

  const renderSideBySideNetworks = (svg: any, diseaseGRN: GRNData, normalGRN: GRNData, width: number, height: number) => {
    const gap = 40;
    const useVerticalLayout = width < 1200;

    let panelWidth, panelHeight, offsetX, offsetY;
    if (useVerticalLayout) {
      // Stack vertically for narrow screens
      panelWidth = width - 40;
      panelHeight = (height - gap) / 2;
      offsetX = 20;
      offsetY = 0;
    } else {
      // Side-by-side for wide screens
      panelWidth = Math.max((width - gap) / 2, 500);  // Minimum 500px per panel
      panelHeight = Math.max(height - 80, 500);       // Minimum 500px height
      offsetX = panelWidth + gap;
      offsetY = 30;
    }

    // Disease network (left or top panel) - use actual disease GRN data
    const diseaseNodes = diseaseGRN.nodes.map((n: any) => ({ ...n }));
    const diseaseEdges = diseaseGRN.edges.map((e: any) => ({ ...e }));
    renderNetworkPanel(svg, diseaseNodes, diseaseEdges, 0, offsetY, panelWidth, panelHeight, 'Disease', true);

    // Normal network (right or bottom panel) - use actual normal GRN data from backend
    const normalNodes = normalGRN.nodes.map((n: any) => ({ ...n }));
    const normalEdges = normalGRN.edges.map((e: any) => ({ ...e }));
    const normalOffsetX = useVerticalLayout ? 0 : offsetX;
    const normalOffsetY = useVerticalLayout ? panelHeight + gap : offsetY;
    renderNetworkPanel(svg, normalNodes, normalEdges, normalOffsetX, normalOffsetY, panelWidth, panelHeight, 'Normal', false);
  };

  const renderNetworkPanel = (
    svg: any, nodes: any[], edges: any[],
    offsetX: number, offsetY: number,
    panelWidth: number, panelHeight: number,
    title: string, isDisease: boolean
  ) => {
    const g = svg.append('g').attr('transform', `translate(${offsetX}, ${offsetY})`);

    // Title with network stats
    const nodeCount = nodes.length;
    const edgeCount = edges.length;
    g.append('text')
      .attr('x', panelWidth / 2)
      .attr('y', 20)
      .attr('text-anchor', 'middle')
      .attr('font-size', '14px')
      .attr('font-weight', 'bold')
      .attr('fill', isDisease ? '#EF4444' : '#10B981')
      .text(`${title} (${nodeCount} nodes, ${edgeCount} edges)`);

    // Border
    g.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', panelWidth)
      .attr('height', panelHeight)
      .attr('fill', 'none')
      .attr('stroke', '#cbd5e1')
      .attr('stroke-width', 1);

    const centerX = panelWidth / 2;
    const centerY = panelHeight / 2;

    // Create inner group for zoom/pan
    const innerG = g.append('g');

    // Add zoom behavior to this panel
    const zoom = d3.zoom<SVGGElement, unknown>()
      .scaleExtent([0.3, 3])
      .on('zoom', (event) => {
        innerG.attr('transform', event.transform);
      });

    g.call(zoom as any);

    // Filter edges to only include those with valid node references
    const nodeIds = new Set(nodes.map((n: any) => n.id));
    const validEdges = edges.filter((e: any) => {
      const sourceId = typeof e.source === 'string' ? e.source : e.source?.id;
      const targetId = typeof e.target === 'string' ? e.target : e.target?.id;
      const isValid = nodeIds.has(sourceId) && nodeIds.has(targetId);
      if (!isValid) {
        console.warn(`Filtering out edge with missing node: ${sourceId} -> ${targetId}`);
      }
      return isValid;
    });

    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(validEdges).id((d: any) => d.id).distance(60))
      .force('charge', d3.forceManyBody().strength(-150))
      .force('center', d3.forceCenter(centerX, centerY))
      .force('collision', d3.forceCollide().radius(15));

    const link = innerG.append('g')
      .selectAll('line')
      .data(validEdges)
      .join('line')
      .attr('stroke', (d: any) => {
        if (d.interaction_type === 'ppi') return '#3B82F6';
        if (d.interaction_type === 'positive') return '#10B981';
        if (d.interaction_type === 'negative') return '#EF4444';
        return '#94a3b8';
      })
      .attr('stroke-width', 1.5)
      .attr('stroke-opacity', 0.5);

    const node = innerG.append('g')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .call(d3.drag<any, any>()
        .on('start', (event, d: any) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d: any) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d: any) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }) as any);

    node.each(function(d: any) {
      const nodeGroup = d3.select(this);
      const isNcRNA = isNonCodingRNA(d);
      const radius = getNodeRadius(d) * 0.8;
      const fillColor = getNodeColor(d);

      if (isNcRNA) {
        const size = radius * 1.3;
        const height = size * 1.5;
        nodeGroup.append('path')
          .attr('d', `M 0,${-height} L ${size},${height/2} L ${-size},${height/2} Z`)
          .attr('fill', fillColor)
          .attr('stroke', '#fff')
          .attr('stroke-width', 1);
      } else {
        nodeGroup.append('circle')
          .attr('r', radius)
          .attr('fill', fillColor)
          .attr('stroke', '#fff')
          .attr('stroke-width', 1);
      }

      nodeGroup.append('text')
        .text(d.label || d.id)
        .attr('x', 0)
        .attr('y', -14)
        .attr('text-anchor', 'middle')
        .attr('font-size', '8px')
        .attr('fill', '#334155');
    });

    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      node.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
    });
  };

  return (
    <div className="w-full h-[600px] bg-gray-900 rounded-lg border border-gray-700 flex flex-col">
      <div className="p-3 border-b border-gray-700 bg-gray-800">
        <div className="flex items-center gap-4 text-sm">
          {/* View Mode Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('disease')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                viewMode === 'disease'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
              }`}
            >
              Disease
            </button>
            {data.has_normal_grn && (
              <button
                onClick={() => setViewMode('normal')}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  viewMode === 'normal'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                }`}
              >
                Normal
              </button>
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-3 ml-4">
            <div className="flex items-center gap-1.5">
              <svg width="12" height="12" viewBox="0 0 12 12">
                <polygon points="6,1 11,11 1,11" fill="#3B82F6" stroke="#fff" strokeWidth="1"/>
              </svg>
              <span className="text-gray-400 text-xs">lncRNA</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-gray-400"></div>
              <span className="text-gray-400 text-xs">Protein-coding</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-orange-500 ring-2 ring-orange-400"></div>
              <span className="text-gray-400 text-xs">FDA Target</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-gray-400 ring-2 ring-red-500"></div>
              <span className="text-gray-400 text-xs">Upregulated (log₂FC &gt; 1)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-gray-400 ring-2 ring-emerald-500"></div>
              <span className="text-gray-400 text-xs">Downregulated (log₂FC &lt; -1)</span>
            </div>
          </div>

          {/* Download Buttons */}
          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={downloadCSV}
              className="px-3 py-1 rounded text-xs font-medium bg-slate-600 text-white hover:bg-slate-700 transition-colors flex items-center gap-1"
              title="Download network data as CSV"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
              </svg>
              CSV
            </button>
            <button
              onClick={downloadPNG}
              className="px-3 py-1 rounded text-xs font-medium bg-slate-600 text-white hover:bg-slate-700 transition-colors flex items-center gap-1"
              title="Download network as PNG"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
              </svg>
              PNG
            </button>
          </div>

          <div className="ml-auto text-gray-500 text-xs">
            {data?.nodes?.length || 0} nodes, {data?.edges?.length || 0} edges
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-hidden relative">
        <svg ref={svgRef} className="w-full h-full"></svg>

        {/* Additional Legend for Edge Types - Only show in single network view */}
        {viewMode === 'disease' && (
          <div className="absolute bottom-2 left-2 bg-gray-800/90 backdrop-blur-sm border border-gray-700 rounded-lg p-2 text-[10px] text-gray-400">
            <div className="font-semibold mb-1 text-gray-300">Edges:</div>
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-0.5 bg-blue-500"></div>
                <span>PPI (solid)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-0.5 border-t-2 border-dashed border-green-500"></div>
                <span>Positive Corr</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-0.5 border-t-2 border-dashed border-red-500"></div>
                <span>Negative Corr</span>
              </div>
              <div className="text-[9px] text-slate-500 mt-1">
                ↑ Arrows show fold change direction
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkGraph;
